import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Zap, Scale, Shield, ShieldCheck, Sliders } from 'lucide-react';

/**
 * S10 — Optimization profile selector.
 *
 * 4 named bundles + a "custom" sentinel for operator-tuned policies.
 * Mirrors PerfectScale's MaxSavings/Balanced/ExtraHeadroom/MaxHeadroom.
 *
 * Backend contract:
 *   GET  /api/v1/rightsizing/profiles  → { profiles: [...] }
 *   POST /api/v1/clusters/{cid}/rightsizing/policy/{pid}/apply-profile
 *        body: { profile_name }
 *   GET  /api/v1/clusters/{cid}/rightsizing/policy/{pid}/effective-profile
 *        → { detected: "balanced" | "custom" }
 */

const PROFILE_ICONS = {
  max_savings:    Zap,
  balanced:       Scale,
  extra_headroom: Shield,
  max_headroom:   ShieldCheck,
};

// Memory risk slider (policy.mem_peak_floor) — the knob that decides how much
// of a workload's burst is RESERVED in the request versus left to the limit.
// Surfaced because it is what actually differentiates the profiles for memory:
// before it existed the percentile was inert for bursty workloads and every
// profile produced the same number.
const MEM_FLOOR_LABELS = {
  p99_peak: {
    label: 'Peak (p99)',
    hint: 'Reserves nearly every observed burst. Safest, most reserved capacity.',
  },
  p95_peak: {
    label: 'Peak (p95)',
    hint: 'Reserves 95% of observed bursts; the rest uses the limit headroom.',
  },
  usage: {
    label: 'Working set',
    hint: 'Reserves typical usage only — bursts run into the limit instead of being reserved. Densest packing.',
  },
};

const PROFILE_COLORS = {
  max_savings:    'bg-emerald-100 text-emerald-800 border-emerald-300',
  balanced:       'bg-blue-100 text-blue-800 border-blue-300',
  extra_headroom: 'bg-amber-100 text-amber-800 border-amber-300',
  max_headroom:   'bg-red-100 text-red-800 border-red-300',
};

export default function RightsizingProfileSelector({ clusterId, policyId, onApplied }) {
  const [profiles, setProfiles] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [error, setError] = useState(null);

  const loadProfiles = useCallback(async () => {
    // The profile catalog is global — fetch it independent of the
    // cluster so the cards render even before a cluster is picked.
    // The effective-profile (active marker) is cluster+policy-scoped
    // and degrades to "custom" if there is no policy doc yet.
    try {
      const profilesResp = await axios.get(`${API}/v1/rightsizing/profiles`);
      setProfiles(profilesResp.data?.profiles || []);
    } catch (err) {
      setError(err?.response?.data?.detail || String(err));
      setLoading(false);
      return;
    }
    if (clusterId && policyId) {
      try {
        const detectedResp = await axios.get(
          `${API}/v1/clusters/${clusterId}/rightsizing/policy/${policyId}/effective-profile`,
        );
        setActive(detectedResp.data?.detected || 'custom');
      } catch {
        // 404 just means no policy doc exists yet; that's fine.
        setActive('custom');
      }
    }
    setLoading(false);
  }, [clusterId, policyId]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const applyProfile = async (name) => {
    if (!clusterId || !policyId) {
      setError('Select a cluster first.');
      return;
    }
    setApplying(name);
    setError(null);
    try {
      await axios.post(
        `${API}/v1/clusters/${clusterId}/rightsizing/policy/${policyId}/apply-profile`,
        { profile_name: name },
      );
      setActive(name);
      if (onApplied) onApplied(name);
    } catch (err) {
      setError(err?.response?.data?.detail || String(err));
    } finally {
      setApplying(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader><CardTitle>Optimization Profile</CardTitle></CardHeader>
        <CardContent><div className="text-sm text-gray-500">Loading…</div></CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Optimization Profile</CardTitle>
          {active === 'custom' && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Sliders className="w-3 h-3" /> Custom (operator-tuned)
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="text-sm text-red-600 mb-3 bg-red-50 px-3 py-2 rounded">
            {error}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {profiles.map((p) => {
            const Icon = PROFILE_ICONS[p.name] || Sliders;
            const isActive = active === p.name;
            return (
              <div
                key={p.name}
                className={`border-2 rounded-lg p-4 cursor-pointer transition ${
                  isActive
                    ? PROFILE_COLORS[p.name] || 'bg-gray-100 border-gray-300'
                    : 'border-gray-200 hover:border-gray-400 bg-white'
                }`}
                onClick={() => !applying && applyProfile(p.name)}
                role="button"
                tabIndex={0}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-5 h-5" />
                  <div className="font-semibold">{p.display_name}</div>
                  {isActive && (
                    <Badge variant="secondary" className="ml-auto text-xs">Active</Badge>
                  )}
                </div>
                <div className="text-xs text-gray-600 mb-3">{p.description}</div>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                  <div className="text-gray-500">CPU headroom:</div>
                  <div className="font-medium">{p.cpu_headroom_pct}%</div>
                  <div className="text-gray-500">Mem headroom:</div>
                  <div className="font-medium">{p.mem_headroom_pct}%</div>
                  <div className="text-gray-500">Percentile:</div>
                  <div className="font-medium">P{p.base_percentile}</div>
                  <div className="text-gray-500">Memory floor:</div>
                  <div
                    className="font-medium"
                    title={(MEM_FLOOR_LABELS[p.mem_peak_floor] || {}).hint || ''}
                  >
                    {(MEM_FLOOR_LABELS[p.mem_peak_floor] || {}).label || p.mem_peak_floor || '—'}
                  </div>
                  <div className="text-gray-500">Apply gate:</div>
                  <div className="font-medium">{p.confidence_apply_band}</div>
                </div>
                {MEM_FLOOR_LABELS[p.mem_peak_floor] && (
                  <div className="mt-2 text-[11px] text-gray-500 leading-snug">
                    {MEM_FLOOR_LABELS[p.mem_peak_floor].hint}
                  </div>
                )}
                <div className="mt-3 pt-2 border-t border-gray-200 text-xs text-gray-500">
                  Best for: {p.suited_for}
                </div>
                <div className="mt-3 flex justify-end">
                  <Button
                    size="sm"
                    variant={isActive ? 'outline' : 'default'}
                    disabled={isActive || applying === p.name}
                  >
                    {applying === p.name
                      ? 'Applying…'
                      : isActive
                      ? 'Active'
                      : 'Apply'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
